import React, { useState, useEffect } from 'react';
import ExecuteAxios from 'Components/ExecuteAxios/ExecuteAxios';
import PanelListDefault from 'Components/PanelListDefault/PanelListDefault';
import Panel from 'Components/Panel/Panel';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import AutomaticallyListedAttributes from 'Components/AutomaticallyListedAttributes/AutomaticallyListedAttributes';
import ApplyAttributes from 'Components/ApplyAttributes/ApplyAttributes';
import RemoveIdsNotFound from 'Components/RemoveIdsNotFound/RemoveIdsNotFound';
import LocalTimeFromInstant from 'Components/LocalTimeFromInstant/LocalTimeFromInstant';

const EmployeeHoursAvailableScheduled = (
    {
        idDaySelected,
        idHoursSelected,
        onUpdateIdHoursSelected,
        onUpdateIdDaySelected,
        onUpdateIdScheduleSelected,
        onUpdateResetFilterDay,
        resetFilterDay
    }) => {
    const [usersHoursDayGrid, setUsersHoursDayGrid] = useState(null);
    const [usersHoursDayDetail, setUsersHoursDayDetail] = useState(null);

    const [employeeDayId, setEmployeeDayId] = useState(null);
    const [planningDate, setPlanningDate] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [employeeName, setEmployeeName] = useState(null);
    const [dtUpdate, setDtUpdate] = useState(null);
    const [userNameUpdate, setUserNameUpdate] = useState(null);

    const [updating, setUpdating] = useState(false);
    const [inserting, setInserting] = useState(false);
    const [loadingUserHourPanel, setLoadingUserHourPanel] = useState(false);

    const readOnlyAttributes = !(inserting || updating);

    const attributeList = [
        { name: "id", value: employeeDayId, setter: setEmployeeDayId, allowsMultipleRecordsSelected: true },
        { name: "planningDate", value: planningDate, setter: setPlanningDate },
        { name: "startTime", value: startTime, setter: setStartTime },
        { name: "endTime", value: endTime, setter: setEndTime },
        { name: "employeeName", value: employeeName, setter: setEmployeeName, readOnly: true },
        { name: "dtUpdate", value: dtUpdate, setter: setDtUpdate },
        { name: "userNameUpdate", value: userNameUpdate, setter: setUserNameUpdate }
    ];

    async function postPeriodCalendar() {
        try {
            const parameters = {
                planningDate: planningDate,
                startTime: LocalTimeFromInstant.getLocalTimeFromInstant(startTime),
                endTime: LocalTimeFromInstant.getLocalTimeFromInstant(endTime)
            };

            setLoadingUserHourPanel(true)
            const response = await ExecuteAxios.api('post', `/employee-hours-available/`, { params: parameters })

            if (response) {
                onUpdateResetFilterDay(true)
            }

            return response;
        } finally {
            setLoadingUserHourPanel(false)
        }
    }

    async function putPeriodCalendar() {
        try {
            setLoadingUserHourPanel(true)
            const response = await ExecuteAxios.api('put', `/employee-hours-available/`, {
                id: employeeDayId,
                startTime: LocalTimeFromInstant.getLocalTimeFromInstant(startTime),
                endTime: LocalTimeFromInstant.getLocalTimeFromInstant(endTime),
            })

            if (response) {
                onUpdateResetFilterDay(true)
            }

            return response;
        } finally {
            setLoadingUserHourPanel(false)
        }
    }

    async function deletePeriodCalendar() {
        const parameters = {
            availableHoursId: idHoursSelected.join(",")
        };

        setLoadingUserHourPanel(true)
        const response = await ExecuteAxios.api('delete', `/employee-hours-available/by-hours-id/`, { params: parameters })
        if (response) {
            onUpdateIdHoursSelected([])
            onUpdateIdDaySelected([])
            onUpdateIdScheduleSelected([])
            setLoadingUserHourPanel(false)
            onUpdateResetFilterDay(true)
        }
        return response
    }

    useEffect(() => {
        getUsersScheduleHoursGrid()
    }, [idDaySelected]);

    useEffect(() => {
        if (!resetFilterDay) {
            return
        }
        getUsersScheduleHoursGrid()
    }, [resetFilterDay]);

    async function getUsersScheduleHoursGrid() {
        if (!idDaySelected || idDaySelected.length === 0) {
            return
        }
        const parameters = {
            hash: idDaySelected[0]
        }

        const listPanel = await ExecuteAxios.api('get', `/employee-hours-available/get-employee-hours-available-grid/`, { params: parameters });
        setUsersHoursDayGrid(listPanel);
        RemoveIdsNotFound.remove(listPanel, onUpdateIdHoursSelected, idHoursSelected)
    }

    useEffect(() => {
        getUsersScheduleHoursDetail()
    }, [idHoursSelected]);

    async function getUsersScheduleHoursDetail() {
        if (!idHoursSelected || idHoursSelected.length === 0) {
            return
        }

        const parameters = {
            id: idHoursSelected[0]
        }

        const listPanel = await ExecuteAxios.api('get', `/employee-hours-available/get-employee-hours-available-detail/`, { params: parameters })
        if (listPanel) {
            setUsersHoursDayDetail(listPanel)
            ApplyAttributes.applyAttributes(listPanel, attributeList)
        }
    }

    const mainPanelStyle = {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    };

    return (
        <>
            <LoadingIndicator loaded={usersHoursDayGrid && !loadingUserHourPanel}>
                <Panel
                    style={mainPanelStyle}
                    canViewCRUD={true}
                    recordsSelected={idDaySelected}
                    inserting={inserting}
                    updating={updating}
                    canNewRecord={false}
                    enableNewRecord={false}
                    postFunction={postPeriodCalendar}
                    canEdit={true}
                    enableEdit={idHoursSelected && idHoursSelected.length === 1}
                    putFunction={putPeriodCalendar}
                    canDelete={true}
                    enableDelete={idHoursSelected && idHoursSelected.length > 0}
                    deleteFunction={deletePeriodCalendar}
                    setUpdating={setUpdating}
                    setInserting={setInserting}
                    dataListedAttributes={usersHoursDayDetail}
                    attributeList={attributeList}
                    onUpdateIdSelected={onUpdateIdHoursSelected}
                >
                    {(updating || inserting)
                        ?
                        <AutomaticallyListedAttributes
                            list={usersHoursDayDetail}
                            attributeList={attributeList}
                            readOnlyAttributes={readOnlyAttributes}
                        />
                        :
                        <PanelListDefault
                            onUpdateIdSelected={onUpdateIdHoursSelected}
                            idsSelected={idHoursSelected}
                            data={usersHoursDayGrid}
                            style={{ height: '100%' }}
                            attributeList={attributeList}
                        />
                    }
                </Panel>
            </LoadingIndicator>

        </>
    )
};

export default EmployeeHoursAvailableScheduled;
